import {useMemo, useCallback, useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {packageTypeByCode} from '../FamilyPackageUsageDetailsPopUp/utils/constants';
import {FetchFamilyBundleUsages} from '../../CellcomAbroadLobbyBlock/Shared/abroadThunk';

const isExpirationMatch = (cycleDataExpirationDate, expirationDate) => {
  if (!cycleDataExpirationDate || !expirationDate) return false;
  const normalize = (d) => d.split(/[\/\.-]/).join('-');
  return normalize(cycleDataExpirationDate) === normalize(expirationDate);
};

const getCycleDataIfExpirationMatches = (cycleData, expirationDate) => {
  if (!cycleData) return {};
  const isExpirationMatchResult = isExpirationMatch(cycleData.expirationDate, expirationDate);
  return isExpirationMatchResult ? cycleData : {};
}

export function useFamilyBundleUsage({showAllTypes = false,SocData = {}, costumerMobile, familyBundleMemberUsageData, familyBundleCollectiveUsageData}) {
  const [familyPackageUsagePopup, setFamilyPackageUsagePopup] = useState(false);

  const subscriberFamilyBundleUsageData = useMemo(() => {
    const cycleData = familyBundleMemberUsageData?.[costumerMobile]?.[SocData?.soc]?.currentCycle;
    return getCycleDataIfExpirationMatches(cycleData, SocData?.expirationDate);
  }, [familyBundleMemberUsageData, costumerMobile, SocData?.soc, SocData?.expirationDate]);

  const subscriberFamilyBundleUsageDataPrevCycle = useMemo(() => {
    const cycleData = familyBundleMemberUsageData?.[costumerMobile]?.[SocData?.soc]?.previousCycle;
    return getCycleDataIfExpirationMatches(cycleData, SocData?.expirationDate);
  }, [familyBundleMemberUsageData, costumerMobile, SocData?.soc, SocData?.expirationDate]);

  const isSubscriberHasFamilyBundleUsageData = subscriberFamilyBundleUsageData?.hasData ?? false;
  const isFamilyBundleManager = subscriberFamilyBundleUsageData?.isManager ?? false;

  const packageKey = useMemo(() => packageTypeByCode[SocData?.ivrCategory], [SocData?.ivrCategory]);

 const getFilteredUsageByType = (usages) => {
    if (!usages) return {};
    if (showAllTypes) return usages;
    return packageKey ? {[packageKey]: usages[packageKey]} : {};
  };

  const popupPackageUsages = useMemo(
    () => getFilteredUsageByType(subscriberFamilyBundleUsageData?.usages),
    [subscriberFamilyBundleUsageData, packageKey, showAllTypes],
  );

  const popupPackageUsagesPrevCycle = useMemo(
    () => getFilteredUsageByType(subscriberFamilyBundleUsageDataPrevCycle?.usages),
    [subscriberFamilyBundleUsageDataPrevCycle, packageKey, showAllTypes],
  );

  const popupCollectiveUsages = useMemo(
    () => getFilteredUsageByType(familyBundleCollectiveUsageData?.[SocData?.soc]?.currentCycle?.usages),
    [familyBundleCollectiveUsageData, SocData?.soc, packageKey, showAllTypes],
  );

  const popupCollectiveUsagesPrevCycle = useMemo(
    () => getFilteredUsageByType(familyBundleCollectiveUsageData?.[SocData?.soc]?.previousCycle?.usages),
    [familyBundleCollectiveUsageData, SocData?.soc, packageKey, showAllTypes],
  );

  const subLabel = isFamilyBundleManager ? 'פירוט לפי מנויים' : isSubscriberHasFamilyBundleUsageData ? 'כמה ניצלתי?' : null;

  const openPopup = useCallback(() => setFamilyPackageUsagePopup(true), []);
  const closePopup = useCallback(() => setFamilyPackageUsagePopup(false), []);

  return {
    subscriberFamilyBundleUsageData,
    isSubscriberHasFamilyBundleUsageData,
    isFamilyBundleManager,
    packageKey,
    popupPackageUsages,
    popupPackageUsagesPrevCycle,
    popupCollectiveUsages,
    popupCollectiveUsagesPrevCycle,
    subLabel,
    familyPackageUsagePopup,
    openPopup,
    closePopup,
  };
}

export const familyBundleMapState = (state) => ({
  costumerMobile: state.selfcarePage?.selectedSubscriber?.productSubscriberNo,
  familyBundleMemberUsageData: state.cellcomAbroad.familyPackageUsageDetails?.data,
  familyBundleCollectiveUsageData: state.cellcomAbroad.familyPackageUsageDetails?.data?.collectiveInfo,
});

export function useFetchFamilyBundle(costumerMobile) {
  const dispatch = useDispatch();
  const familyBundleMemberUsageData = useSelector((state) => state.cellcomAbroad.familyPackageUsageDetails?.data);
  const isPending = useSelector((state) => state.cellcomAbroad.familyPackageUsageDetails.isPending);
  const subscribersWithNoApiData = useSelector((state) => state.cellcomAbroad.familyPackageUsageDetails.subscribersWithNoApiData);

  useEffect(() => {
    const isReadyToFetch =
      costumerMobile &&
      !(familyBundleMemberUsageData && costumerMobile in familyBundleMemberUsageData) &&
      !isPending &&
      !subscribersWithNoApiData.includes(costumerMobile);
    if (isReadyToFetch) {
      dispatch(FetchFamilyBundleUsages({subscriber: costumerMobile}));
    }
  }, [costumerMobile, familyBundleMemberUsageData]);
}
